// @ts-nocheck
"use client"

import { useState, useEffect, useRef, type ReactNode } from "react"
import _ from "lodash"
import { Button } from "@/components/ui/button"
import SidePanel from "./SidePanel"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { GripVertical, Trophy, Plus, Settings } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"

interface TiebreakerConfig {
  id: string
  maxPointsScored?: number | null
  maxPointDifferential?: number | null
  hidden?: boolean
}

// Tiebreaker settings that are NOT per-sport. They live on `config` directly
// (not under `config.tiebreakers[sport]`) and are saved alongside whichever
// sport tab the admin clicks Save on. An object rather than a bare boolean so
// the next sport-agnostic setting needs no signature change.
interface GeneralTiebreakerSettings {
  breakPlayoffTiesBySeed?: boolean
}

const normalizeGeneral = (g?: GeneralTiebreakerSettings): GeneralTiebreakerSettings => ({
  breakPlayoffTiesBySeed: !!g?.breakPlayoffTiesBySeed,
})

interface Tiebreaker {
  id: string
  name: string
  description: string
  hasConfig?: boolean
  configFields?: string[]
}

interface Sport {
  id: string
  name: string
  icon: string
  activeTiebreakers: (Tiebreaker & { config?: TiebreakerConfig })[]
  availableTiebreakers: Tiebreaker[]
}

const initialSportsData: Sport[] = [
  {
    id: "football",
    name: "Football",
    icon: "🏈",
    activeTiebreakers: [{ id: "h2h", name: "Head-to-Head Record", description: "Record in games between tied teams" }],
    availableTiebreakers: [
      { id: "division", name: "Division Record", description: "Record within division games" },
      { id: "conference", name: "Conference Record", description: "Record within conference games" },
      { id: "common", name: "Common Games", description: "Record in games against common opponents" },
      { id: "strength", name: "Strength of Victory", description: "Combined record of defeated opponents" },
      { id: "schedule", name: "Strength of Schedule", description: "Combined record of all opponents" },
    ],
  },
  {
    id: "basketball",
    name: "Basketball",
    icon: "🏀",
    activeTiebreakers: [{ id: "headToHead", name: "Head-to-Head Record", description: "Record in games between tied teams" }],
    availableTiebreakers: [
      { 
        id: "pointsScored", 
        name: "Points Scored", 
        description: "Total points scored in all games",
        hasConfig: true,
        configFields: ["maxPointsScored"]
      },
      { id: "pointsAllowed", name: "Points Allowed", description: "Total points allowed in all games" },
      { 
        id: "pointDifferential", 
        name: "Point Differential", 
        description: "Points scored minus points allowed",
        hasConfig: true,
        configFields: ["maxPointDifferential"]
      },
    ],
  },
  {
    id: "baseball",
    name: "Baseball",
    icon: "⚾",
    // INT-0028: modernized IN PLACE (entry order is pinned by the defaultTab
    // test) — the old h2h/division/last-N ids were never honored by the
    // standings engine. Engine-implemented ids only, run vocabulary, same
    // shape as softball.
    activeTiebreakers: [{ id: "headToHead", name: "Head-to-Head Record", description: "Record in games between tied teams" }],
    availableTiebreakers: [
      {
        id: "pointDifferential",
        name: "Run Differential",
        description: "Runs scored minus runs allowed",
        hasConfig: true,
        configFields: ["maxPointDifferential"],
      },
      {
        id: "pointsScored",
        name: "Runs Scored",
        description: "Total runs scored across all games",
        hasConfig: true,
        configFields: ["maxPointsScored"],
      },
      { id: "pointsAllowed", name: "Runs Allowed", description: "Total runs allowed across all games" },
    ],
  },
  {
    id: "soccer",
    name: "Soccer",
    icon: "⚽",
    // Engine-implemented ids only (standings.ts sorts pointsScored /
    // pointDifferential / pointsAllowed / headToHead) — the previous
    // goal-diff/goals-for/away-goals/fair-play ids were never honored by the
    // standings engine. Labels keep the goal vocabulary.
    activeTiebreakers: [{ id: "headToHead", name: "Head-to-Head Record", description: "Record in games between tied teams" }],
    availableTiebreakers: [
      {
        id: "pointDifferential",
        name: "Goal Difference",
        description: "Goals scored minus goals conceded",
        hasConfig: true,
        configFields: ["maxPointDifferential"],
      },
      {
        id: "pointsScored",
        name: "Goals Scored",
        description: "Total goals scored across all games",
        hasConfig: true,
        configFields: ["maxPointsScored"],
      },
      { id: "pointsAllowed", name: "Goals Conceded", description: "Total goals conceded across all games" },
    ],
  },
  {
    id: "volleyball",
    name: "Volleyball",
    icon: "🏐",
    // Game scores store SETS WON (set-by-set detail lives in setScores), so
    // "points" here means sets.
    activeTiebreakers: [{ id: "headToHead", name: "Head-to-Head Record", description: "Record in matches between tied teams" }],
    availableTiebreakers: [
      {
        id: "pointDifferential",
        name: "Set Differential",
        description: "Sets won minus sets lost",
        hasConfig: true,
        configFields: ["maxPointDifferential"],
      },
      {
        id: "pointsScored",
        name: "Sets Won",
        description: "Total sets won across all matches",
        hasConfig: true,
        configFields: ["maxPointsScored"],
      },
      { id: "pointsAllowed", name: "Sets Lost", description: "Total sets lost across all matches" },
    ],
  },
  {
    id: "pickleball",
    name: "Pickleball",
    icon: "🏓",
    activeTiebreakers: [{ id: "headToHead", name: "Head-to-Head Record", description: "Record in games between tied teams" }],
    availableTiebreakers: [
      {
        id: "pointDifferential",
        name: "Point Differential",
        description: "Points scored minus points allowed",
        hasConfig: true,
        configFields: ["maxPointDifferential"],
      },
      {
        id: "pointsScored",
        name: "Points Scored",
        description: "Total points scored across all games",
        hasConfig: true,
        configFields: ["maxPointsScored"],
      },
      { id: "pointsAllowed", name: "Points Allowed", description: "Total points allowed across all games" },
      { id: "gamesWon", name: "Games Won", description: "Total games won" },
    ],
  },
  // Softball (INT-0025): APPENDED last — the defaultTab test pins the order of
  // the entries above. Engine-implemented ids only (soccer's modernized shape,
  // NOT baseball's inert h2h/last-N ids); labels use run vocabulary.
  {
    id: "softball",
    name: "Softball",
    icon: "🥎",
    activeTiebreakers: [{ id: "headToHead", name: "Head-to-Head Record", description: "Record in games between tied teams" }],
    availableTiebreakers: [
      {
        id: "pointDifferential",
        name: "Run Differential",
        description: "Runs scored minus runs allowed",
        hasConfig: true,
        configFields: ["maxPointDifferential"],
      },
      {
        id: "pointsScored",
        name: "Runs Scored",
        description: "Total runs scored across all games",
        hasConfig: true,
        configFields: ["maxPointsScored"],
      },
      { id: "pointsAllowed", name: "Runs Allowed", description: "Total runs allowed across all games" },
    ],
  },
]

function SortableItem({
  tiebreaker,
  index,
  onRemove,
  onConfigChange,
}: { 
  tiebreaker: Tiebreaker & { config?: TiebreakerConfig }; 
  index: number; 
  onRemove: (id: string) => void;
  onConfigChange: (id: string, config: TiebreakerConfig) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tiebreaker.id })
  console.log('tiebreaker: ', tiebreaker);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleMaxPointsChange = (value: string) => {
    const numValue = value === "" ? null : parseInt(value, 10)
    onConfigChange(tiebreaker.id, {
      ...tiebreaker.config,
      id: tiebreaker.id,
      maxPointsScored: numValue
    })
  }

  const handleMaxPointDifferentialChange = (value: string) => {
    const numValue = value === "" ? null : parseInt(value, 10)
    onConfigChange(tiebreaker.id, {
      ...tiebreaker.config,
      id: tiebreaker.id,
      maxPointDifferential: numValue
    })
  }

  const handleHiddenChange = (checked: boolean) => {
    onConfigChange(tiebreaker.id, {
      ...tiebreaker.config,
      id: tiebreaker.id,
      hidden: checked
    })
  }

  return (
    <div ref={setNodeRef} style={style} className={`${isDragging ? "opacity-50" : ""}`}>
      <Card className="mb-2 hover:shadow-md transition-shadow">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
            <Badge variant="outline" className="w-2 h-6 items-center justify-center">
              {index + 1}
            </Badge>
            <div className="flex-1">
              <h4 className="font-medium text-sm">{tiebreaker.name}</h4>
              <p className="text-xs text-muted-foreground mt-1">{tiebreaker.description}</p>
              {tiebreaker.hasConfig && tiebreaker.configFields?.includes("maxPointsScored") && (
                <div className="mt-2 flex items-center gap-2">
                  <Label htmlFor={`maxPoints-${tiebreaker.id}`} className="text-xs text-muted-foreground">
                    Max Points:
                  </Label>
                  <Input
                    id={`maxPoints-${tiebreaker.id}`}
                    type="number"
                    min="0"
                    placeholder="No limit"
                    value={tiebreaker.config?.maxPointsScored || ""}
                    onChange={(e) => handleMaxPointsChange(e.target.value)}
                    className="w-20 h-6 text-xs"
                  />
                </div>
              )}
              {tiebreaker.hasConfig && tiebreaker.configFields?.includes("maxPointDifferential") && (
                <div className="mt-2 flex items-center gap-2">
                  <Label htmlFor={`maxPointDiff-${tiebreaker.id}`} className="text-xs text-muted-foreground">
                    Max Point Diff:
                  </Label>
                  <Input
                    id={`maxPointDiff-${tiebreaker.id}`}
                    type="number"
                    min="0"
                    placeholder="No limit"
                    value={tiebreaker.config?.maxPointDifferential || ""}
                    onChange={(e) => handleMaxPointDifferentialChange(e.target.value)}
                    className="w-20 h-6 text-xs"
                  />
                </div>
              )}
              <div className="mt-2 flex items-center gap-2">
                <Label htmlFor={`hidden-${tiebreaker.id}`} className="text-xs text-muted-foreground">
                  Hide in Standings:
                </Label>
                <Switch
                  id={`hidden-${tiebreaker.id}`}
                  checked={tiebreaker.config?.hidden || false}
                  onCheckedChange={handleHiddenChange}
                />
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRemove(tiebreaker.id)}
              className="text-muted-foreground hover:text-destructive"
            >
              ×
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

function TiebreakerList({
  sport,
  onReorder,
  onAddTiebreaker,
  onRemoveTiebreaker,
  onConfigChange,
  onSave,
  hasChanges,
}: {
  sport: Sport
  onReorder: (sportId: string, tiebreakers: (Tiebreaker & { config?: TiebreakerConfig })[]) => void
  onAddTiebreaker: (sportId: string, tiebreakerId: string) => void
  onRemoveTiebreaker: (sportId: string, tiebreakerId: string) => void
  onConfigChange: (sportId: string, tiebreakerId: string, config: TiebreakerConfig) => void
  onSave: (sportId: string) => void
  hasChanges: boolean
}) {
  const [selectedTiebreaker, setSelectedTiebreaker] = useState<string>("")

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (active.id !== over?.id) {
      const oldIndex = sport.activeTiebreakers.findIndex((item) => item.id === active.id)
      const newIndex = sport.activeTiebreakers.findIndex((item) => item.id === over?.id)

      const newTiebreakers = arrayMove(sport.activeTiebreakers, oldIndex, newIndex)
      onReorder(sport.id, newTiebreakers)
    }
  }

  const handleAddTiebreaker = () => {
    if (selectedTiebreaker) {
      onAddTiebreaker(sport.id, selectedTiebreaker)
      setSelectedTiebreaker("")
    }
  }

  const handleConfigChange = (tiebreakerId: string, config: TiebreakerConfig) => {
    onConfigChange(sport.id, tiebreakerId, config)
  }

  return (
    <div className="space-y-4 max-w-2xl mx-auto">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-2xl">{sport.icon}</span>
        <div>
          <h3 className="font-semibold">{sport.name} Tiebreakers</h3>
          <p className="text-sm text-muted-foreground">Drag to reorder tiebreaker priority</p>
        </div>
      </div>

      {sport.activeTiebreakers.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <p className="text-muted-foreground mb-4">No tiebreakers configured</p>
            <p className="text-sm text-muted-foreground">Add a tiebreaker below to get started</p>
          </CardContent>
        </Card>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sport.activeTiebreakers.map((t) => t.id)} strategy={verticalListSortingStrategy}>
            {sport.activeTiebreakers.map((tiebreaker, index) => (
              <SortableItem
                key={tiebreaker.id}
                tiebreaker={tiebreaker}
                index={index}
                onRemove={onRemoveTiebreaker.bind(null, sport.id)}
                onConfigChange={handleConfigChange}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {sport.availableTiebreakers.length > 0 && (
        <Card className="border-dashed">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <Select value={selectedTiebreaker} onValueChange={setSelectedTiebreaker}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder="Add a tiebreaker..." />
                </SelectTrigger>
                <SelectContent>
                  {sport.availableTiebreakers.map((tiebreaker) => (
                    <SelectItem key={tiebreaker.id} value={tiebreaker.id}>
                      {tiebreaker.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleAddTiebreaker} disabled={!selectedTiebreaker} size="sm" className="gap-1">
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end pt-4 border-t">
        <Button onClick={() => onSave(sport.id)} className="gap-2" disabled={!hasChanges}>
          Save {sport.name} Configuration
        </Button>
      </div>
    </div>
  )
}

export default function TiebreakerModal({ 
  open,
  onClose,
  title = "Sports Tiebreaker Configuration",
  subtitle,
  onSubmit,
  sports = ["football", "basketball", "baseball", "soccer", "pickleball", "volleyball", "softball"],
  tiebreakers = {},
  generalSettings = {},
  seedInheritedFrom = null,
  tiebreakersInheritedFrom = null
}: {
  open: boolean
  onClose: () => void
  title?: string
  subtitle?: ReactNode
  onSubmit?: (
    sportId: string,
    tiebreakerConfigs: TiebreakerConfig[],
    generalSettings: GeneralTiebreakerSettings,
  ) => void
  sports?: string[]
  tiebreakers?: Record<string, TiebreakerConfig[]>
  generalSettings?: GeneralTiebreakerSettings
  // Non-null when the seeded playoff-seed toggle value comes from a PARENT
  // (this entity has no own value) — names the level it inherits from.
  seedInheritedFrom?: string | null
  // Non-null when the seeded tiebreaker CHAIN comes from a parent (this
  // entity has no own non-empty chain) — names the level it inherits from.
  tiebreakersInheritedFrom?: string | null
}) {
  // Initialize sports data with provided tiebreakers
  const initializeSportsData = (tiebreakerConfig: Record<string, TiebreakerConfig[]>): Sport[] => {
    return initialSportsData.map(sport => {
      const tiebreakerConfigs = tiebreakerConfig[sport.id] || []
      
      // Get all available tiebreakers for this sport
      const allTiebreakers = [...sport.activeTiebreakers, ...sport.availableTiebreakers]
      
      // Find tiebreakers that should be active based on the provided configuration
      // IMPORTANT: Maintain the order from the database configuration
      const activeTiebreakers: (Tiebreaker & { config?: TiebreakerConfig })[] = []
      const availableTiebreakers: Tiebreaker[] = []
      
      // First, process tiebreakers in the order they appear in the database config
      tiebreakerConfigs.forEach(config => {
        const tiebreaker = allTiebreakers.find(t => t.id === config.id)
        if (tiebreaker) {
          // Ensure config has default values for missing properties
          const configWithDefaults = {
            ...config,
            hidden: config.hidden !== undefined ? config.hidden : false
          }
          activeTiebreakers.push({ ...tiebreaker, config: configWithDefaults })
        }
      })
      
      // Then, add any remaining tiebreakers to available list
      allTiebreakers.forEach(tiebreaker => {
        const isActive = activeTiebreakers.some(active => active.id === tiebreaker.id)
        if (!isActive) {
          availableTiebreakers.push(tiebreaker)
        }
      })
      
      return {
        ...sport,
        activeTiebreakers,
        availableTiebreakers
      }
    })
  }

  const [sportsData, setSportsData] = useState<Sport[]>(initializeSportsData(tiebreakers))
  const [originalSportsData, setOriginalSportsData] = useState<Sport[]>(initializeSportsData(tiebreakers))

  // Update sports data when tiebreakers prop *content* changes. The default
  // `tiebreakers = {}` creates a new object literal on every render, so a
  // shallow `[tiebreakers]` dep would fire every render and wipe in-flight
  // edits (e.g. immediately undoing a user's "+ Add" click).
  const lastTiebreakersRef = useRef(tiebreakers)
  useEffect(() => {
    if (_.isEqual(lastTiebreakersRef.current, tiebreakers)) return
    lastTiebreakersRef.current = tiebreakers
    const updatedSportsData = initializeSportsData(tiebreakers)
    setSportsData(updatedSportsData)
    setOriginalSportsData(updatedSportsData)
  }, [tiebreakers])

  const [general, setGeneral] = useState<GeneralTiebreakerSettings>(normalizeGeneral(generalSettings))
  const [originalGeneral, setOriginalGeneral] = useState<GeneralTiebreakerSettings>(normalizeGeneral(generalSettings))

  // Same deep-compare guard as the tiebreakers sync above, for the same reason:
  // the default `generalSettings = {}` is a fresh literal every render, so a
  // shallow dep would re-fire constantly and undo an in-flight toggle.
  const lastGeneralRef = useRef(generalSettings)
  useEffect(() => {
    if (_.isEqual(lastGeneralRef.current, generalSettings)) return
    lastGeneralRef.current = generalSettings
    const next = normalizeGeneral(generalSettings)
    setGeneral(next)
    setOriginalGeneral(next)
  }, [generalSettings])

  const generalChanged = () => !_.isEqual(general, originalGeneral)

  // Filter sports data based on the sports prop
  const filteredSportsData = sportsData.filter(sport => sports.includes(sport.id))
  const defaultTab = filteredSportsData.length > 0 ? filteredSportsData[0].id : "football"

  // The two empty states below deliberately omit the "Break playoff ties by
  // higher seed" switch: they render no Save button, so a toggle there could
  // never be persisted.
  // If no sports are provided or sports array is empty, show a message
  if (!sports || sports.length === 0) {
    return (
      <SidePanel
        open={open}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        description="No sports configured for this tournament. Please add sports to the tournament before configuring tiebreakers."
        footer={<Button className="ml-auto" onClick={onClose}>Close</Button>}
      >
        <div />
      </SidePanel>
    )
  }

  // If no supported sports are found in the tiebreaker system, show a message
  if (filteredSportsData.length === 0) {
    return (
      <SidePanel
        open={open}
        onClose={onClose}
        title={title}
        subtitle={subtitle}
        description={`The selected sports (${sports.join(', ')}) are not supported for tiebreaker configuration. Currently supported sports are: basketball, football, baseball, softball, soccer, volleyball, and pickleball.`}
        footer={<Button className="ml-auto" onClick={onClose}>Close</Button>}
      >
        <div />
      </SidePanel>
    )
  }

  const handleReorder = (sportId: string, newTiebreakers: (Tiebreaker & { config?: TiebreakerConfig })[]) => {
    setSportsData((prev) =>
      prev.map((sport) => (sport.id === sportId ? { ...sport, activeTiebreakers: newTiebreakers } : sport)),
    )
  }

  const handleAddTiebreaker = (sportId: string, tiebreakerId: string) => {
    setSportsData((prev) =>
      prev.map((sport) => {
        if (sport.id === sportId) {
          const tiebreakerToAdd = sport.availableTiebreakers.find((t) => t.id === tiebreakerId)
          if (tiebreakerToAdd) {
            return {
              ...sport,
              activeTiebreakers: [...sport.activeTiebreakers, { ...tiebreakerToAdd, config: { id: tiebreakerId, hidden: false } }],
              availableTiebreakers: sport.availableTiebreakers.filter((t) => t.id !== tiebreakerId),
            }
          }
        }
        return sport
      }),
    )
  }

  const handleRemoveTiebreaker = (sportId: string, tiebreakerId: string) => {
    setSportsData((prev) =>
      prev.map((sport) => {
        if (sport.id === sportId) {
          const tiebreakerToRemove = sport.activeTiebreakers.find((t) => t.id === tiebreakerId)
          if (tiebreakerToRemove) {
            const { config, ...tiebreakerWithoutConfig } = tiebreakerToRemove
            return {
              ...sport,
              activeTiebreakers: sport.activeTiebreakers.filter((t) => t.id !== tiebreakerId),
              availableTiebreakers: [...sport.availableTiebreakers, tiebreakerWithoutConfig],
            }
          }
        }
        return sport
      }),
    )
  }

  const handleConfigChange = (sportId: string, tiebreakerId: string, config: TiebreakerConfig) => {
    setSportsData((prev) =>
      prev.map((sport) => {
        if (sport.id === sportId) {
          return {
            ...sport,
            activeTiebreakers: sport.activeTiebreakers.map((tiebreaker) =>
              tiebreaker.id === tiebreakerId ? { ...tiebreaker, config } : tiebreaker
            ),
          }
        }
        return sport
      }),
    )
  }

  const hasChanges = (sportId: string) => {
    const currentSport = sportsData.find((s) => s.id === sportId)
    const originalSport = originalSportsData.find((s) => s.id === sportId)

    if (!currentSport || !originalSport) return false

    // Check if active tiebreakers are different
    if (currentSport.activeTiebreakers.length !== originalSport.activeTiebreakers.length) {
      return true
    }

    // Check if the order or content of active tiebreakers changed
    for (let i = 0; i < currentSport.activeTiebreakers.length; i++) {
      const current = currentSport.activeTiebreakers[i]
      const original = originalSport.activeTiebreakers[i]
      
      if (current.id !== original?.id) return true
      
      // Check if config changed
      if (current.config?.maxPointsScored !== original?.config?.maxPointsScored) return true
      if (current.config?.maxPointDifferential !== original?.config?.maxPointDifferential) return true
      if (current.config?.hidden !== original?.config?.hidden) return true
    }

    return false
  }

  const handleSave = (sportId: string) => {
    const currentSport = sportsData.find((s) => s.id === sportId)
    
    // Call onSubmit with the sport ID and list of tiebreaker configs if provided
    if (onSubmit && currentSport) {
      const tiebreakerConfigs = currentSport.activeTiebreakers.map(tiebreaker => tiebreaker.config || { id: tiebreaker.id })
      onSubmit(sportId, tiebreakerConfigs, general)
    }

    // Update the original data to match current data
    setOriginalSportsData((prev) =>
      prev.map((sport) => (sport.id === sportId ? sportsData.find((s) => s.id === sportId) || sport : sport)),
    )
    setOriginalGeneral(general)

    // Close the modal
    onClose()
  }

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      description="Manage tiebreaker hierarchies for different sports. Add tiebreakers and drag to reorder priority. Configure settings for tiebreakers that support them. The playoff tie rule at the top applies to every sport."
      className="sm:max-w-2xl"
    >
        {/* Sport-agnostic, so it sits ABOVE the per-sport tabs — it must not read
            as a rule for whichever sport happens to be selected. It's persisted
            by whichever tab's Save button the admin clicks. */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                {/* Config key keeps its legacy name (`breakPlayoffTiesBySeed`) —
                    the tie basis is the standings position only; manual seeds
                    never decide a tie (stakeholder decision 2026-08-21). */}
                <Label htmlFor="breakPlayoffTiesBySeed" className="text-sm font-medium">
                  Break playoff ties by standings position
                </Label>
                <p className="mt-1 text-xs text-muted-foreground">
                  Applies to all sports. When a playoff game ends in a tie, the team placed higher in
                  the regular-season / pool-play standings is shown as the winner and advances in the
                  bracket. A tie between teams with no standings position stays undecided. Manually
                  assigned seeds never break a tie. Regular-season ties are unaffected, and a 0–0
                  game is treated as unplayed.
                </p>
                {seedInheritedFrom && (
                  <p className="mt-1 text-xs text-muted-foreground/60">
                    Inherited from {seedInheritedFrom}. Switching it here overrides that.
                  </p>
                )}
              </div>
              <Switch
                id="breakPlayoffTiesBySeed"
                checked={!!general.breakPlayoffTiesBySeed}
                onCheckedChange={(checked) =>
                  setGeneral((prev) => ({ ...prev, breakPlayoffTiesBySeed: checked }))}
              />
            </div>
          </CardContent>
        </Card>

        {tiebreakersInheritedFrom && (
          <p className="mb-2 text-xs text-muted-foreground/60">
            These tiebreakers are inherited from {tiebreakersInheritedFrom}. Editing here
            overrides them for this level. Removing all rules reverts to the inherited order.
          </p>
        )}

        <Tabs defaultValue={defaultTab} className="w-full">
          <TabsList className="grid w-full" style={{ gridTemplateColumns: `repeat(${filteredSportsData.length}, 1fr)` }}>
            {filteredSportsData.map((sport) => (
              <TabsTrigger key={sport.id} value={sport.id} className="gap-1">
                <span>{sport.icon}</span>
                <span className="hidden sm:inline">{sport.name}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <div className="mt-4 max-h-[600px] overflow-y-auto pr-2">
            {filteredSportsData.map((sport) => (
              <TabsContent key={sport.id} value={sport.id} className="mt-0">
                <TiebreakerList
                  sport={sport}
                  onReorder={handleReorder}
                  onAddTiebreaker={handleAddTiebreaker}
                  onRemoveTiebreaker={handleRemoveTiebreaker}
                  onConfigChange={handleConfigChange}
                  onSave={handleSave}
                  // OR'd, not folded into hasChanges(): a general-only edit must
                  // enable Save on EVERY visible tab, since any of them persists it.
                  hasChanges={hasChanges(sport.id) || generalChanged()}
                />
              </TabsContent>
            ))}
          </div>
        </Tabs>
    </SidePanel>
  )
}
